import { BrowserModule } from '@angular/platform-browser';
import { APP_INITIALIZER, Injector, NgModule, NO_ERRORS_SCHEMA } from '@angular/core';
import {
  LocationStrategy,
  HashLocationStrategy,
  CommonModule,
} from '@angular/common';
import { BsDropdownModule } from 'ngx-bootstrap/dropdown';
import { TabsModule } from 'ngx-bootstrap/tabs';
import { HTTP_INTERCEPTORS, HttpClientModule } from '@angular/common/http';
import { AuthService } from './auth/auth.service';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { HttpAuthInterceptor } from './interceptors/http-auth.interceptor';
import { AuthGuardService } from './auth-guard.service';
import { SignupComponent } from './auth/signup/signup.component';
import { OAuthModule } from 'angular-oauth2-oidc';
import { HttpErrorInterceptor } from './interceptors/http-error.interceptor';
import {
  DEFAULT_TIMEOUT,
  TimeoutInterceptor,
} from './interceptors/http-timeout.interceptor';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { ToastrModule } from 'ngx-toastr';
import { SettingsMenuComponent } from './components/settings-menu/settings-menu.component';
import { NgxPermissionsModule } from 'ngx-permissions';
import { NgbModule } from '@ng-bootstrap/ng-bootstrap';
import { LoadingModule } from 'ngx-loading';
import { NavigationEnd, Router, RouterModule } from '@angular/router';
import { AppComponent } from './app.component';
import { ChartModule, Highcharts } from 'angular-highcharts';

// Import components
import {
  AppAsideComponent,
  AppBreadcrumbsComponent,
  AppFooterComponent,
  AppHeaderComponent,
  AppSidebarComponent,
  AppSidebarFooterComponent,
  AppSidebarFormComponent,
  AppSidebarHeaderComponent,
  AppSidebarMinimizerComponent,
  AppWhatsNewComponent,
  APP_SIDEBAR_NAV,
} from './components';

const APP_COMPONENTS = [
  AppAsideComponent,
  AppBreadcrumbsComponent,
  AppFooterComponent,
  AppHeaderComponent,
  AppSidebarComponent,
  AppSidebarFooterComponent,
  AppSidebarFormComponent,
  AppSidebarHeaderComponent,
  AppSidebarMinimizerComponent,
  APP_SIDEBAR_NAV,
  AppWhatsNewComponent,
];

// Import directives
import {
  AsideToggleDirective,
  NAV_DROPDOWN_DIRECTIVES,
  SIDEBAR_TOGGLE_DIRECTIVES,
} from './directives';

const APP_DIRECTIVES = [
  AsideToggleDirective,
  NAV_DROPDOWN_DIRECTIVES,
  SIDEBAR_TOGGLE_DIRECTIVES,
];

// Import routing module
import { TransactionService } from './services/transaction.service';
import { UserService } from './services/user.service';
import { PermissionsService } from './views/rights/permissions/permissions.service';
import { UserlogService } from './services/userlog.service';
import { CompaniesModule } from './views/companies/companies.module';
import { TransactionsModule } from './views/transactions/transactions.module';
import { CustomersModule } from './views/customers/customers.module';
import { VendorsModule } from './views/vendors/vendors.module';
import { UserModule } from './views/user/user.module';
import { RightsModule } from './views/rights/rights.module';
import { EdiTypeModule } from './views/editype/editype.module';
import { ImportHistoryModule } from './views/import-history/import-history.module';
import { ConsoleApplicationsModule } from './views/console-applications/console-applications.module';
import { TradingPartnersModule } from './views/trading-partners/trading-partners.module';
import { routes } from './app.routing';
import { RolesService } from './views/rights/roles/roles.service';
import { onAppInit } from './interceptors/initializer.interceptor';
import { NgxSmartModalModule } from 'ngx-smart-modal';
import { MaterialModule } from './views/material/material.module';
import { OrderHeadersModule } from './views/order-headers/order-headers.module';
import { ShipmentHeadersModule } from './views/shipment-headers/shipment-headers.module';
import { ShipmentLinesModalComponent } from './views/shipment-headers/shipment-lines-modal/shipment-lines-modal.component';
import { TableModule } from 'primeng/table';
import { FileUploadModule } from 'primeng/fileupload';
import {
  MatDialogModule,
  MatRadioModule,
  MatSlideToggleModule,
} from '@angular/material';
import { OrderHeadersFaqModalComponent } from './views/order-headers/order-headers-faq-modal/order-headers-faq-modal.component';
import { InvoicingLinesModalComponent } from './views/invoicing-headers/invoicing-lines-modal/invoicing-lines-modal.component';
import { InvoicingHeadersModule } from './views/invoicing-headers/invoicing-headers.module';
import { ConfirmationService } from 'primeng/api';
import { ConfirmDialogModule } from 'primeng/primeng';
import { SidebarModule } from 'primeng/sidebar';
import { OrderLinesModule } from './views/order-lines/order-lines.module';
import { OesModule } from './views/oes/oes.module';
import { MasterCustomerFormComponent } from './views/oes/master-customer/master-customer-form/master-customer-form.component';
import { MergeCustomersModule } from './views/merge-customers/merge-customers.module';
import { MergeConfirmationModalComponent } from './views/merge-customers/merge-confirmation-modal/merge-confirmation-modal.component';
import { MergeVendorsModule } from './views/merge-vendors/merge-vendors.module';
import { WorkspaceSelectComponent } from './views/oes/select-workspace/workspace-select.component';
import {MatAutocompleteModule} from '@angular/material/autocomplete';
import {NgMultiSelectDropDownModule} from 'ng-multiselect-dropdown';
import {NgxMatSelectSearchModule} from 'ngx-mat-select-search';
import {ImportMasterDataModalComponent} from './components/import-master-data-modal/import-master-data-modal.component';
import {ExportMasterDataModalComponent} from './components/export-master-data-modal/export-master-data-modal.component';
import {FileExportsModule} from './views/file-exports/file-exports.module';
import {PipesModule} from './views/pipes/pipes.module';
import {AppPartUsageTimeSeriesComponent} from './components/app-part-usage-time-series/app-part-usage-time-series.component';
import {PasswordResetModule} from './auth/password-reset/password-reset.module';
import {MobileModule} from './modules/mobile/mobile.module';
import {ThresholdModalComponent} from './components/threshold-modal/threshold-modal.component';
import {UploadTradingPartnerComponent} from './components';
import {WhatsNewModule} from './views/whatsnew/whatsnew.module';
import {TradingPartnerSaveModalComponent} from './views/trading-partners/trading-partner-save-modal/trading-partner-save-modal.component';
import {ZeroOutInventoryModule} from './views/zero-out-inventory/zero-out-inventory.module';
import {TransactionsEmailModalComponent} from './views/transactions/transactions-email-modal/transactions-email-modal.component';
import {TransactionReportsModule} from './views/transaction-reports/transaction-reports.module';
import {TeamworkReportsModule} from './views/teamwork-reports/teamwork-reports.module';

@NgModule({
  imports: [
    BrowserModule,
    NgxSmartModalModule.forRoot(),
    HttpClientModule,
    OAuthModule.forRoot(),
    RouterModule.forRoot(routes),
    BsDropdownModule.forRoot(),
    TabsModule.forRoot(),
    NgbModule.forRoot(),
    BrowserAnimationsModule,
    ToastrModule.forRoot(),
    FormsModule,
    ReactiveFormsModule,
    CommonModule,
    NgxPermissionsModule.forRoot(),
    LoadingModule,
    CompaniesModule,
    TransactionsModule,
    CustomersModule,
    VendorsModule,
    UserModule,
    TradingPartnersModule,
    RightsModule,
    MaterialModule,
    MatAutocompleteModule,
    NgMultiSelectDropDownModule,
    NgxMatSelectSearchModule,
    EdiTypeModule,
    ImportHistoryModule,
    ConsoleApplicationsModule,
    OrderHeadersModule,
    OrderLinesModule,
    MergeCustomersModule,
    MergeVendorsModule,
    ShipmentHeadersModule,
    InvoicingHeadersModule,
    MatDialogModule,
    TableModule,
    FileUploadModule,
    ConfirmDialogModule,
    SidebarModule,
    OesModule,
    PasswordResetModule,
    MatRadioModule,
    FileExportsModule,
    MatSlideToggleModule,
    PipesModule,
    ChartModule,
    MobileModule,
    WhatsNewModule,
    ZeroOutInventoryModule,
    TransactionReportsModule,
      TeamworkReportsModule
  ],
  declarations: [
    AppComponent,
    ...APP_COMPONENTS,
    ...APP_DIRECTIVES,
    SignupComponent,
    SettingsMenuComponent,
    ShipmentLinesModalComponent,
    OrderHeadersFaqModalComponent,
    InvoicingLinesModalComponent,
    MasterCustomerFormComponent,
    WorkspaceSelectComponent,
    ImportMasterDataModalComponent,
    ExportMasterDataModalComponent,
    AppPartUsageTimeSeriesComponent,
    ThresholdModalComponent,
    UploadTradingPartnerComponent,
  ],
  entryComponents: [
    ShipmentLinesModalComponent,
    OrderHeadersFaqModalComponent,
    InvoicingLinesModalComponent,
    MergeConfirmationModalComponent,
    ImportMasterDataModalComponent,
    ExportMasterDataModalComponent,
    AppPartUsageTimeSeriesComponent,
    ThresholdModalComponent,
    UploadTradingPartnerComponent,
    TradingPartnerSaveModalComponent,
    TransactionsEmailModalComponent
  ],
  providers: [
    {
      provide: LocationStrategy,
      useClass: HashLocationStrategy,
    },
    AuthService,
    TransactionService,
    UserService,
    UserlogService,
    PermissionsService,
    RolesService,
    {
      provide: APP_INITIALIZER,
      useFactory: onAppInit,
      deps: [Injector],
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: HttpAuthInterceptor,
      multi: true,
    },
    { provide: HTTP_INTERCEPTORS, useClass: HttpErrorInterceptor, multi: true },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: TimeoutInterceptor,
      multi: true,
    },
    { provide: DEFAULT_TIMEOUT, useValue: 10000 },
    ConfirmationService,
    AuthGuardService
  ],
  bootstrap: [AppComponent],
  schemas: [NO_ERRORS_SCHEMA]
})
export class AppModule {
  constructor(private router: Router) {
    this.router.events.subscribe((path) => {
      if (path instanceof NavigationEnd) {
        if (window['NProgress']) {
          window['NProgress'].set(1);
          setTimeout(function () {
            if (window['NProgress']) {
              window['NProgress'].done(true);
              window['NProgress'].remove();
              delete window['NProgress'];
            }
            window['NProgress'] = null;
          }, 2000);
        }
      }
    });
  }
}



// WEBPACK FOOTER //
// /home/ubuntu/edi/angular/app/app.module.ts